// components/ExcelFileUpload.tsx
import React, { useRef, useCallback } from 'react';
import * as XLSX from 'xlsx';

interface ExcelFileUploadProps<T> {
  onDataParsed: (data: T[]) => void;
  onError?: (error: string) => void;
  onFileSelect?: (fileName: string) => void;
  parseRow: (row: Record<string, any>, headers: string[]) => T | null;
  accept?: string;
  disabled?: boolean;
  children?: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}

export function ExcelFileUpload<T>({
  onDataParsed,
  onError,
  onFileSelect,
  parseRow,
  accept = '.xlsx,.xls,.csv',
  disabled = false,
  children,
  className,
  style,
}: ExcelFileUploadProps<T>) {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      onFileSelect?.(file.name);

      try {
        const arrayBuffer = await file.arrayBuffer();
        const workbook = XLSX.read(arrayBuffer, { type: 'array' });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];

        // Get raw data as array of arrays
        const rawData = XLSX.utils.sheet_to_json<any[]>(worksheet, { header: 1 });

        if (rawData.length < 2) {
          onError?.('File appears to be empty or has no data rows');
          return;
        }

        // Extract and normalize headers
        const headers = (rawData[0] as any[]).map((h) =>
          String(h || '').toLowerCase().trim()
        );

        // Parse each row using the provided parseRow function
        const parsedData: T[] = [];
        const errors: string[] = [];

        for (let i = 1; i < rawData.length; i++) {
          const row = rawData[i] as any[];
          
          // Skip completely empty rows
          if (!row || row.every((cell) => cell === undefined || cell === '' || cell === null)) {
            continue;
          }

          // Convert array row to object with header keys
          const rowObject: Record<string, any> = {};
          headers.forEach((header, index) => {
            rowObject[header] = row[index];
          });

          const parsed = parseRow(rowObject, headers);
          if (parsed) {
            parsedData.push(parsed);
          } else {
            errors.push(`Row ${i + 1}`);
          }
        }

        if (errors.length > 0 && errors.length <= 5) {
          onError?.(`Some rows were skipped: ${errors.join(', ')}`);
        } else if (errors.length > 5) {
          onError?.(`${errors.length} rows were skipped due to invalid data`);
        }

        if (parsedData.length === 0) {
          onError?.('No valid data found in the file');
          return;
        }

        onDataParsed(parsedData);
      } catch (error) {
        console.error('Error parsing file:', error);
        onError?.('Failed to parse file. Please ensure it\'s a valid Excel or CSV file');
      }

      // Reset input so same file can be selected again
      if (inputRef.current) {
        inputRef.current.value = '';
      }
    },
    [onDataParsed, onError, onFileSelect, parseRow]
  );

  const handleClick = () => {
    inputRef.current?.click();
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={handleFileChange}
        disabled={disabled}
        style={{ display: 'none' }}
      />
      <div
        onClick={disabled ? undefined : handleClick}
        className={className}
        style={{
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.6 : 1,
          ...style,
        }}
      >
        {children || (
          <button type="button" disabled={disabled}>
            Select File
          </button>
        )}
      </div>
    </>
  );
}